import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { salesService } from '../../services/salesService';

const initialState = {
  deals: [],
  currentDeal: null,
  testDrives: [],
  quotes: [],
  pipelineData: null,
  stats: null,
  loading: false,
  error: null,
  pagination: {
    count: 0,
    next: null,
    previous: null,
  },
};

// Async thunks for deals
export const fetchDeals = createAsyncThunk(
  'sales/fetchDeals',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await salesService.getDeals(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch deals');
    }
  }
);

export const fetchDeal = createAsyncThunk(
  'sales/fetchDeal',
  async (id, { rejectWithValue }) => {
    try {
      const data = await salesService.getDeal(id);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch deal');
    }
  }
);

export const createDeal = createAsyncThunk(
  'sales/createDeal',
  async (dealData, { rejectWithValue }) => {
    try {
      const data = await salesService.createDeal(dealData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create deal');
    }
  }
);

export const updateDeal = createAsyncThunk(
  'sales/updateDeal',
  async ({ id, dealData }, { rejectWithValue }) => {
    try {
      const data = await salesService.updateDeal(id, dealData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to update deal');
    }
  }
);

export const deleteDeal = createAsyncThunk(
  'sales/deleteDeal',
  async (id, { rejectWithValue }) => {
    try {
      await salesService.deleteDeal(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to delete deal');
    }
  }
);

// Test drives
export const fetchTestDrives = createAsyncThunk(
  'sales/fetchTestDrives',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await salesService.getTestDrives(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch test drives');
    }
  }
);

export const createTestDrive = createAsyncThunk(
  'sales/createTestDrive',
  async (testDriveData, { rejectWithValue }) => {
    try {
      const data = await salesService.createTestDrive(testDriveData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create test drive');
    }
  }
);

// Quotes
export const fetchQuotes = createAsyncThunk(
  'sales/fetchQuotes',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await salesService.getQuotes(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch quotes');
    }
  }
);

export const createQuote = createAsyncThunk(
  'sales/createQuote',
  async (quoteData, { rejectWithValue }) => {
    try {
      const data = await salesService.createQuote(quoteData);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create quote');
    }
  }
);

// Pipeline and stats
export const fetchPipelineData = createAsyncThunk(
  'sales/fetchPipelineData',
  async (_, { rejectWithValue }) => {
    try {
      const data = await salesService.getPipelineData();
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch pipeline data');
    }
  }
);

export const fetchSalesStats = createAsyncThunk(
  'sales/fetchStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const data = await salesService.getSalesStats(params);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch sales stats');
    }
  }
);

const salesSlice = createSlice({
  name: 'sales',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentDeal: (state) => {
      state.currentDeal = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch deals
      .addCase(fetchDeals.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDeals.fulfilled, (state, action) => {
        state.loading = false;
        state.deals = action.payload.results || action.payload;
        state.pagination = {
          count: action.payload.count || action.payload.length,
          next: action.payload.next || null,
          previous: action.payload.previous || null,
        };
      })
      .addCase(fetchDeals.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Single deal
      .addCase(fetchDeal.fulfilled, (state, action) => {
        state.currentDeal = action.payload;
      })
      // Create deal
      .addCase(createDeal.fulfilled, (state, action) => {
        state.deals.unshift(action.payload);
      })
      // Update deal
      .addCase(updateDeal.fulfilled, (state, action) => {
        const index = state.deals.findIndex(deal => deal.id === action.payload.id);
        if (index !== -1) {
          state.deals[index] = action.payload;
        }
        if (state.currentDeal?.id === action.payload.id) {
          state.currentDeal = action.payload;
        }
      })
      // Delete deal
      .addCase(deleteDeal.fulfilled, (state, action) => {
        state.deals = state.deals.filter(deal => deal.id !== action.payload);
      })
      // Test drives
      .addCase(fetchTestDrives.fulfilled, (state, action) => {
        state.testDrives = action.payload.results || action.payload;
      })
      .addCase(createTestDrive.fulfilled, (state, action) => {
        state.testDrives.unshift(action.payload);
      })
      // Quotes
      .addCase(fetchQuotes.fulfilled, (state, action) => {
        state.quotes = action.payload.results || action.payload;
      })
      .addCase(createQuote.fulfilled, (state, action) => {
        state.quotes.unshift(action.payload);
      })
      // Pipeline data
      .addCase(fetchPipelineData.fulfilled, (state, action) => {
        state.pipelineData = action.payload;
      })
      // Sales stats
      .addCase(fetchSalesStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearError, clearCurrentDeal } = salesSlice.actions;
export default salesSlice.reducer;